import { getProjectModelRevision } from '../project/project_revision_service.js';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';

const DEFAULT_LAYER = 'KS_EXPORT';
const MM_INSUNITS = 4;
const DEFAULT_TEXT_HEIGHT = 90;
const DEFAULT_TEXT_STYLE = 'ARIAL_UNICODE_MS';
const DEFAULT_TEXT_FONT = 'Arial Unicode MS';
const MAX_DXF_SHAPES_PER_OBJECT = 20_000;
const MAX_DXF_POINTS_PER_SHAPE = 100_000;
const MAX_DXF_ENTITIES_PER_SYSTEM = 250_000;
const MAX_DXF_COORDINATE_ABS = 1_000_000_000;
const MAX_DXF_TEXT_LENGTH = 1_000;
const CORE_COLOR_BY_LAYER = Object.freeze({
  KS_EXPORT: 7,
  OBSTACLES_PIPE: 4,
  OBSTACLES_DUCT: 6,
  OBSTACLES_WALL: 8,
  KS_TEXT: 7,
  KS_WARNINGS: 1
});

async function downloadProject(options = {}) {
  const result = await buildProjectDxf(options);
  downloadText(result.filename, result.content, 'application/dxf');
  return result;
}

async function buildProjectDxf(options = {}) {
  const project = options.project || currentProject();
  const entities = [];
  const blocks = [];
  const warnings = [];
  const liveProject = currentProject();
  const initialRevision = getProjectModelRevision();
  const initialSheetId = S.drawingSheets?.activeSheetId;
  const canCollectSystemObjects = !liveProject || project === liveProject;
  const excludedSystemIds = [...new Set(
    Array.isArray(options.excludeSystemIds) ? options.excludeSystemIds : []
  )];

  // Systemkonturen koennen von lazy nachgeladenen neutralen DXF-Artefakten
  // abhaengen. Jeder Owner wird separat vorbereitet; ein fehlerhaftes System
  // wird fuer diesen Export ausgeschlossen, ohne andere Systeme zu blockieren.
  if (canCollectSystemObjects) {
    const readiness = await SystemRegistry.ensureAllDxfShapesReady({
      project,
      excludeSystemIds: excludedSystemIds,
      timeoutMs: options.readinessTimeoutMs
    });
    for (const status of readiness?.statuses || []) {
      if (status?.status !== 'failed') continue;
      excludedSystemIds.push(status.systemId);
      warnings.push(`System ${shortWarningText(status.systemId)} wurde nicht exportiert, weil seine DXF-Daten nicht bereit waren.`);
    }
  } else {
    warnings.push('Systembeitraege wurden nicht exportiert, weil der angegebene Projektstand nicht der aktiven Sitzung entspricht.');
  }

  if (canCollectSystemObjects && (currentProject() !== liveProject
      || getProjectModelRevision() !== initialRevision || S.drawingSheets?.activeSheetId !== initialSheetId)) {
    throw new Error('Projekt oder Zeichnungsebene wurde während der DXF-Vorbereitung geändert. Export bitte erneut starten.');
  }

  if (canCollectSystemObjects) {
    collectSystemObjectEntities(entities, blocks, warnings, {
      project,
      excludeSystemIds: excludedSystemIds
    });
  }
  collectObstacleEntities(project, entities, warnings);

  const hasGeometry = blocks.some(block => hasExportableGeometry(block.entities))
    || hasExportableGeometry(entities);
  if (!hasGeometry) {
    throw new Error('Keine exportierbare 2D-Geometrie gefunden.');
  }

  const content = serializeDxf(entities, blocks, {
    project,
    warnings,
    filename: options.filename
  });
  const summary = summarizeEntities(entities, blocks, warnings);
  return {
    filename: options.filename || defaultFilename(project),
    content,
    summary,
    warnings
  };
}

function hasExportableGeometry(entities) {
  return (entities || []).some(entity => ['line', 'polyline', 'circle', 'arc', 'text'].includes(entity.type));
}

// Jedes platzierte Objekt (Artikel) wird als eigener DXF-BLOCK + INSERT
// exportiert, statt seine Linien/Polylines/Texte lose in ENTITIES zu
// dumpen -- Nutzerwunsch 2026-08-19: ein Artikel soll in CAD-Software als
// EIN Element markierbar sein, nicht nur eine einzelne Linie davon. Die
// Shapes sind in den einzelnen Systemen bereits vollstaendig in
// Welt-/Plankoordinaten (inkl. Rotation) berechnet -- daher braucht der
// Block selbst keine eigene Rotation/Skalierung (INSERT bleibt bei 0/1):
// die Geometrie wird nur um den lokalen Block-Ursprung (Bounding-Box-
// Ecke) verschoben, INSERT platziert sie an derselben Weltposition zurueck.
function collectSystemObjectEntities(entities, blocks, warnings, options = {}) {
  const objects = SystemRegistry.collectProjectObjects(options) || [];
  const entityCountByOwner = new Map();
  let blockSeq = 0;
  for (const object of objects) {
    const ownerId = authoritativeSystemOwner(object);
    if (!ownerId) {
      warnings.push('Ein Systemobjekt ohne eindeutigen Owner wurde fuer DXF uebersprungen.');
      continue;
    }
    const localEntities = [];
    const added = addProjectObjectEntity(localEntities, object, ownerId);
    if (!added) {
      const id = shortWarningText(object?.id || object?.refId || object?.articleNo || object?.type || 'unbekannt');
      warnings.push(`Objekt ${id} wurde fuer DXF uebersprungen.`);
      continue;
    }
    const previousEntityCount = entityCountByOwner.get(ownerId) || 0;
    if (previousEntityCount + localEntities.length > MAX_DXF_ENTITIES_PER_SYSTEM) {
      warnings.push(`Weitere DXF-Geometrie von System ${shortWarningText(ownerId)} wurde wegen des Ausgabelimits uebersprungen.`);
      continue;
    }
    entityCountByOwner.set(ownerId, previousEntityCount + localEntities.length);
    const bounds = entityBounds(localEntities);
    const anchor = bounds ? { x: bounds.minX, y: bounds.minY } : { x: 0, y: 0 };
    const layer = primaryLayerForObject(object, ownerId);
    const name = uniqueBlockName(object, ownerId, blockSeq++);
    blocks.push({
      name,
      layer,
      entities: localEntities.map(entity => shiftEntity(entity, anchor.x, anchor.y))
    });
    entities.push({
      type: 'insert',
      layer,
      block: name,
      point: anchor
    });
  }
}

function primaryLayerForObject(_object, ownerId) {
  return systemLayerName(ownerId, 'GEOMETRY');
}

function uniqueBlockName(object, ownerId, index) {
  const base = cleanText(objectLabel(object) || object.type || 'ARTIKEL')
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 15);
  return `S${stableNameHash(ownerId)}_${base || 'ARTIKEL'}_${index}`.slice(0, 31);
}

function shiftPoint(point, dx, dy) {
  return { x: point.x - dx, y: point.y - dy };
}

function shiftEntity(entity, dx, dy) {
  if (entity.type === 'line') {
    return { ...entity, start: shiftPoint(entity.start, dx, dy), end: shiftPoint(entity.end, dx, dy) };
  }
  if (entity.type === 'polyline') {
    return { ...entity, points: entity.points.map(point => shiftPoint(point, dx, dy)) };
  }
  if (entity.type === 'circle' || entity.type === 'arc') {
    return { ...entity, center: shiftPoint(entity.center, dx, dy) };
  }
  if (entity.type === 'text') {
    return { ...entity, point: shiftPoint(entity.point, dx, dy) };
  }
  return entity;
}

function addProjectObjectEntity(entities, object, ownerId) {
  if (!object || typeof object !== 'object') return false;
  const layer = primaryLayerForObject(object, ownerId);
  const label = objectLabel(object);
  const dxfShapeCount = addProjectObjectDxfShapes(entities, object, ownerId);
  if (dxfShapeCount > 0) return true;

  if (isFiniteNumber(object.x) && isFiniteNumber(object.z) && isFiniteNumber(object.width) && isFiniteNumber(object.depth)) {
    addRectangleFromCenter(entities, {
      layer,
      center: { x: Number(object.x), y: Number(object.z) },
      width: Number(object.width),
      depth: Number(object.depth),
      rotation: Number(object.rotation) || 0,
      label,
      labelLayer: systemLayerName(ownerId, 'TEXT'),
      labelStyle: systemTextStyleName(ownerId)
    });
    return true;
  }

  const footprint = object.footprint && typeof object.footprint === 'object' ? object.footprint : null;
  if (footprint?.mode === 'corner'
    && isFiniteNumber(footprint.x)
    && isFiniteNumber(footprint.width)
    && isFiniteNumber(footprint.depth)) {
    addRectangleFromCorner(entities, {
      layer,
      origin: {
        x: Number(footprint.x),
        y: isFiniteNumber(footprint.z) ? Number(footprint.z) : Number(footprint.y) || 0
      },
      width: Number(footprint.width),
      depth: Number(footprint.depth),
      rotation: Number(footprint.rotation) || 0,
      label,
      labelLayer: systemLayerName(ownerId, 'TEXT'),
      labelStyle: systemTextStyleName(ownerId)
    });
    return true;
  }

  if (footprint?.mode === 'center'
    && isFiniteNumber(footprint.x)
    && isFiniteNumber(footprint.width)
    && isFiniteNumber(footprint.depth)) {
    addRectangleFromCenter(entities, {
      layer,
      center: {
        x: Number(footprint.x),
        y: isFiniteNumber(footprint.z) ? Number(footprint.z) : Number(footprint.y) || 0
      },
      width: Number(footprint.width),
      depth: Number(footprint.depth),
      rotation: Number(footprint.rotation) || 0,
      label,
      labelLayer: systemLayerName(ownerId, 'TEXT'),
      labelStyle: systemTextStyleName(ownerId)
    });
    return true;
  }

  return false;
}

function addProjectObjectDxfShapes(entities, object, ownerId) {
  const shapes = Array.isArray(object?.dxfShapes) ? object.dxfShapes : [];
  let added = 0;
  for (const shape of shapes.slice(0, MAX_DXF_SHAPES_PER_OBJECT)) {
    if (!shape || typeof shape !== 'object') continue;
    const layer = systemLayerName(ownerId, shape.layer || 'GEOMETRY');
    if (shape.type === 'text') {
      const point = pointFromPlan(shape.point || shape.labelPoint);
      if (!point || !shape.text && !shape.label) continue;
      addText(entities, systemLayerName(ownerId, shape.labelLayer || 'TEXT'), point, shape.text || shape.label, shape.rotation || shape.labelRotation || 0, {
        height: shape.height || shape.textHeight || shape.labelHeight,
        style: systemTextStyleName(ownerId, shape.style || shape.textStyle),
        font: shape.font || shape.textFont
      });
      added += 1;
    } else if (shape.type === 'circle') {
      const center = pointFromPlan(shape.center || shape.point);
      const radius = Number(shape.radius);
      if (!center || !(radius > 0)) continue;
      addCircle(entities, layer, center, radius);
      added += 1;
    } else if (shape.type === 'arc') {
      const center = pointFromPlan(shape.center || shape.point);
      const radius = Number(shape.radius);
      if (!center || !(radius > 0)) continue;
      addArc(entities, layer, center, radius, shape.startAngle, shape.endAngle);
      added += 1;
    } else if (shape.type === 'line') {
      const start = pointFromPlan(shape.start);
      const end = pointFromPlan(shape.end);
      if (!start || !end) continue;
      addLine(entities, layer, start, end);
      added += 1;
    } else if (shape.type === 'polyline') {
      const points = Array.isArray(shape.points)
        ? shape.points.slice(0, MAX_DXF_POINTS_PER_SHAPE).map(pointFromPlan).filter(Boolean)
        : [];
      if (points.length < 2) continue;
      addPolyline(entities, layer, points, { closed: shape.closed !== false });
      added += 1;
      if (shape.label) {
        const labelPoint = pointFromPlan(shape.labelPoint) || centroid(points);
        addText(entities, systemLayerName(ownerId, shape.labelLayer || 'TEXT'), labelPoint, shape.label, shape.labelRotation || 0, {
          height: shape.labelHeight,
          style: systemTextStyleName(ownerId, shape.labelStyle),
          font: shape.labelFont
        });
      }
    }
  }
  return added;
}

function collectObstacleEntities(project, entities, warnings) {
  const obstacles = Array.isArray(project?.obstacles) ? project.obstacles : [];
  for (const obstacle of obstacles) {
    if (!obstacle?.start || !obstacle?.end) continue;
    const start = point2(obstacle.start.x, obstacle.start.z);
    const end = point2(obstacle.end.x, obstacle.end.z);
    if (!start || !end) continue;

    const type = String(obstacle.type || '').trim();
    const layer = type === 'pipe'
      ? 'OBSTACLES_PIPE'
      : type === 'duct'
        ? 'OBSTACLES_DUCT'
        : 'OBSTACLES_WALL';
    const width = obstacleWidth(obstacle);

    if (type === 'pipe') {
      addLine(entities, layer, start, end);
      addText(entities, 'KS_TEXT', midpoint(start, end), obstacleLabel(obstacle), 90);
      continue;
    }

    if (width > 0) {
      addRectangleAlongSegment(entities, {
        layer,
        start,
        end,
        width,
        label: obstacleLabel(obstacle)
      });
    } else {
      addLine(entities, layer, start, end);
      warnings.push(`Hindernis ${obstacle.id || type || ''} wurde nur als Mittellinie exportiert.`);
    }
  }
}

function addRectangleFromCenter(entities, options) {
  const halfW = Math.max(0.5, Math.abs(options.width) / 2);
  const halfD = Math.max(0.5, Math.abs(options.depth) / 2);
  const local = [
    { x: -halfW, y: -halfD },
    { x: halfW, y: -halfD },
    { x: halfW, y: halfD },
    { x: -halfW, y: halfD }
  ];
  const points = local.map(point => rotateAround(
    { x: options.center.x + point.x, y: options.center.y + point.y },
    options.center,
    options.rotation
  ));
  addClosedPolylineAsLines(entities, options.layer, points);
  if (options.label) addText(entities, options.labelLayer || 'KS_TEXT', options.center, options.label, options.rotation, {
    style: options.labelStyle
  });
}

function addRectangleFromCorner(entities, options) {
  const width = Math.max(1, Math.abs(options.width));
  const depth = Math.max(1, Math.abs(options.depth));
  const origin = options.origin;
  const points = [
    { x: origin.x, y: origin.y },
    { x: origin.x + width, y: origin.y },
    { x: origin.x + width, y: origin.y + depth },
    { x: origin.x, y: origin.y + depth }
  ].map(point => rotateAround(point, origin, options.rotation));
  addClosedPolylineAsLines(entities, options.layer, points);
  if (options.label) addText(entities, options.labelLayer || 'KS_TEXT', centroid(points), options.label, options.rotation, {
    style: options.labelStyle
  });
}

function addRectangleAlongSegment(entities, options) {
  const dx = options.end.x - options.start.x;
  const dy = options.end.y - options.start.y;
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) return;
  const nx = -dy / length;
  const ny = dx / length;
  const half = Math.max(0.5, Math.abs(options.width) / 2);
  const points = [
    { x: options.start.x + nx * half, y: options.start.y + ny * half },
    { x: options.end.x + nx * half, y: options.end.y + ny * half },
    { x: options.end.x - nx * half, y: options.end.y - ny * half },
    { x: options.start.x - nx * half, y: options.start.y - ny * half }
  ];
  addClosedPolylineAsLines(entities, options.layer, points);
  if (options.label) addText(entities, 'KS_TEXT', midpoint(options.start, options.end), options.label, Math.atan2(dy, dx) * 180 / Math.PI);
}

function addClosedPolylineAsLines(entities, layer, points) {
  addPolyline(entities, layer, points, { closed: true });
}

function addPolyline(entities, layer, points, options = {}) {
  const cleanPoints = (points || []).map(point => point2(point?.x, point?.y)).filter(Boolean);
  if (cleanPoints.length < 2) return;
  entities.push({
    type: 'polyline',
    layer: layerName(layer),
    points: cleanPoints,
    closed: options.closed === true
  });
}

function addLine(entities, layer, start, end) {
  const cleanStart = point2(start?.x, start?.y);
  const cleanEnd = point2(end?.x, end?.y);
  if (!cleanStart || !cleanEnd) return;
  entities.push({
    type: 'line',
    layer: layerName(layer),
    start: cleanStart,
    end: cleanEnd
  });
}

function addCircle(entities, layer, center, radius) {
  const cleanCenter = point2(center?.x, center?.y);
  if (!cleanCenter || !(Number(radius) > 0) || Number(radius) > MAX_DXF_COORDINATE_ABS) return;
  entities.push({
    type: 'circle',
    layer: layerName(layer),
    center: cleanCenter,
    radius: Number(radius)
  });
}

function addArc(entities, layer, center, radius, startAngle = 0, endAngle = 0) {
  const cleanCenter = point2(center?.x, center?.y);
  if (!cleanCenter || !(Number(radius) > 0) || Number(radius) > MAX_DXF_COORDINATE_ABS) return;
  entities.push({
    type: 'arc',
    layer: layerName(layer),
    center: cleanCenter,
    radius: Number(radius),
    startAngle: Number.isFinite(Number(startAngle)) ? Number(startAngle) : 0,
    endAngle: Number.isFinite(Number(endAngle)) ? Number(endAngle) : 0
  });
}

function addText(entities, layer, point, text, rotation = 0, options = {}) {
  const value = cleanText(text).slice(0, MAX_DXF_TEXT_LENGTH);
  const cleanPoint = point2(point?.x, point?.y);
  if (!value || !cleanPoint) return;
  const requestedHeight = Number(options.height);
  entities.push({
    type: 'text',
    layer: layerName(layer),
    point: cleanPoint,
    text: value,
    height: Number.isFinite(requestedHeight) && requestedHeight > 0
      ? Math.min(requestedHeight, MAX_DXF_COORDINATE_ABS)
      : DEFAULT_TEXT_HEIGHT,
    rotation: Number.isFinite(Number(rotation)) ? Number(rotation) : 0,
    style: textStyleName(options.style || DEFAULT_TEXT_STYLE),
    font: cleanText(options.font || DEFAULT_TEXT_FONT).slice(0, 255) || DEFAULT_TEXT_FONT
  });
}

function serializeDxf(entities, blocks, options = {}) {
  const writer = createWriter();
  const blockEntities = (blocks || []).flatMap(block => block.entities);
  const allEntities = [...entities, ...blockEntities];
  const layers = [...new Set([
    ...allEntities.map(entity => layerName(entity.layer)),
    ...(blocks || []).map(block => layerName(block.layer)),
    'KS_TEXT'
  ])].sort();

  writer.group(0, 'SECTION');
  writer.group(2, 'HEADER');
  writer.group(9, '$ACADVER');
  writer.group(1, 'AC1009');
  writer.group(9, '$INSUNITS');
  writer.group(70, MM_INSUNITS);
  writer.group(0, 'ENDSEC');

  writer.group(0, 'SECTION');
  writer.group(2, 'TABLES');
  writer.group(0, 'TABLE');
  writer.group(2, 'LAYER');
  writer.group(70, layers.length);
  for (const layer of layers) {
    writer.group(0, 'LAYER');
    writer.group(2, layer);
    writer.group(70, 0);
    writer.group(62, colorForLayer(layer));
    writer.group(6, 'CONTINUOUS');
  }
  writer.group(0, 'ENDTAB');
  writeTextStyleTable(writer, allEntities);
  writer.group(0, 'ENDSEC');

  writer.group(0, 'SECTION');
  writer.group(2, 'BLOCKS');
  for (const block of blocks || []) {
    writer.group(0, 'BLOCK');
    writer.group(8, layerName(block.layer));
    writer.group(2, block.name);
    writer.group(70, 0);
    writer.group(10, 0);
    writer.group(20, 0);
    writer.group(30, 0);
    writer.group(3, block.name);
    writeEntitiesGroup(writer, block.entities);
    writer.group(0, 'ENDBLK');
    writer.group(8, layerName(block.layer));
  }
  writer.group(0, 'ENDSEC');

  writer.group(0, 'SECTION');
  writer.group(2, 'ENTITIES');
  for (const entity of entities) {
    if (entity.type === 'insert') writeInsert(writer, entity);
    else writeEntity(writer, entity);
  }
  writeExportMetadata(writer, { ...options, entities });
  writer.group(0, 'ENDSEC');
  writer.group(0, 'EOF');
  return writer.toString();
}

function writeEntitiesGroup(writer, entities) {
  for (const entity of entities || []) writeEntity(writer, entity);
}

function writeEntity(writer, entity) {
  if (entity.type === 'line') writeLine(writer, entity);
  if (entity.type === 'polyline') writePolyline(writer, entity);
  if (entity.type === 'circle') writeCircle(writer, entity);
  if (entity.type === 'arc') writeArc(writer, entity);
  if (entity.type === 'text') writeText(writer, entity);
}

function writeInsert(writer, entity) {
  const point = dxfPoint(entity.point);
  if (!point || !entity.block) return;
  writer.group(0, 'INSERT');
  writer.group(8, layerName(entity.layer));
  writer.group(2, entity.block);
  writer.group(10, point.x);
  writer.group(20, point.y);
  writer.group(30, 0);
  writer.group(41, 1);
  writer.group(42, 1);
  writer.group(43, 1);
  writer.group(50, 0);
}

function writeTextStyleTable(writer, entities) {
  const styles = new Map();
  styles.set(DEFAULT_TEXT_STYLE, DEFAULT_TEXT_FONT);
  for (const entity of entities) {
    if (entity.type !== 'text') continue;
    styles.set(textStyleName(entity.style || DEFAULT_TEXT_STYLE), cleanText(entity.font || DEFAULT_TEXT_FONT) || DEFAULT_TEXT_FONT);
  }
  writer.group(0, 'TABLE');
  writer.group(2, 'STYLE');
  writer.group(70, styles.size);
  for (const [styleName, fontName] of styles.entries()) {
    writer.group(0, 'STYLE');
    writer.group(2, styleName);
    writer.group(70, 0);
    writer.group(40, 0);
    writer.group(41, 1);
    writer.group(50, 0);
    writer.group(71, 0);
    writer.group(42, 2.5);
    writer.group(3, fontName);
    writer.group(4, '');
  }
  writer.group(0, 'ENDTAB');
}

function writeLine(writer, entity) {
  const start = dxfPoint(entity.start);
  const end = dxfPoint(entity.end);
  if (!start || !end) return;
  writer.group(0, 'LINE');
  writer.group(8, entity.layer);
  writer.group(10, start.x);
  writer.group(20, start.y);
  writer.group(30, 0);
  writer.group(11, end.x);
  writer.group(21, end.y);
  writer.group(31, 0);
}

function writePolyline(writer, entity) {
  const points = (entity.points || []).map(dxfPoint).filter(Boolean);
  if (points.length < 2) return;
  writer.group(0, 'POLYLINE');
  writer.group(8, entity.layer);
  writer.group(66, 1);
  writer.group(70, entity.closed ? 1 : 0);
  writer.group(10, 0);
  writer.group(20, 0);
  writer.group(30, 0);
  for (const point of points) {
    writer.group(0, 'VERTEX');
    writer.group(8, entity.layer);
    writer.group(10, point.x);
    writer.group(20, point.y);
    writer.group(30, 0);
  }
  writer.group(0, 'SEQEND');
}

function writeCircle(writer, entity) {
  const center = dxfPoint(entity.center);
  if (!center || !(Number(entity.radius) > 0)) return;
  writer.group(0, 'CIRCLE');
  writer.group(8, entity.layer);
  writer.group(10, center.x);
  writer.group(20, center.y);
  writer.group(30, 0);
  writer.group(40, entity.radius);
}

function writeArc(writer, entity) {
  const center = dxfPoint(entity.center);
  if (!center || !(Number(entity.radius) > 0)) return;
  writer.group(0, 'ARC');
  writer.group(8, entity.layer);
  writer.group(10, center.x);
  writer.group(20, center.y);
  writer.group(30, 0);
  writer.group(40, entity.radius);
  writer.group(50, dxfRotation(entity.endAngle || 0));
  writer.group(51, dxfRotation(entity.startAngle || 0));
}

function writeText(writer, entity) {
  const point = dxfPoint(entity.point);
  if (!point) return;
  writer.group(0, 'TEXT');
  writer.group(8, entity.layer);
  writer.group(10, point.x);
  writer.group(20, point.y);
  writer.group(30, 0);
  writer.group(40, entity.height || 90);
  writer.group(7, entity.style || DEFAULT_TEXT_STYLE);
  writer.group(1, entity.text);
  writer.group(50, dxfRotation(entity.rotation || 0));
}

function writeExportMetadata(writer, options = {}) {
  const warnings = Array.isArray(options.warnings) ? options.warnings : [];
  const base = metadataAnchor(options.entities || []);
  addMetadataText(writer, base, 'KS Studio DXF Export');
  if (warnings.length) {
    warnings.slice(0, 6).forEach((warning, index) => {
      addMetadataText(writer, { x: base.x, y: base.y + 120 * (index + 1) }, `Hinweis: ${warning}`);
    });
  }
}

function addMetadataText(writer, point, text) {
  writeText(writer, {
    type: 'text',
    layer: 'KS_TEXT',
    point,
    text: cleanText(text),
    height: 60,
    rotation: 0,
    style: DEFAULT_TEXT_STYLE,
    font: DEFAULT_TEXT_FONT
  });
}

function createWriter() {
  const lines = [];
  return {
    group(code, value) {
      lines.push(String(code));
      lines.push(formatValue(value));
    },
    toString() {
      return `${lines.join('\r\n')}\r\n`;
    }
  };
}

function downloadText(filename, content, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

function summarizeEntities(entities, blocks, warnings) {
  const blockEntities = (blocks || []).flatMap(block => block.entities);
  const combined = [...entities, ...blockEntities];
  return {
    lineCount: combined.filter(entity => entity.type === 'line').length,
    polylineCount: combined.filter(entity => entity.type === 'polyline').length,
    circleCount: combined.filter(entity => entity.type === 'circle').length,
    arcCount: combined.filter(entity => entity.type === 'arc').length,
    textCount: combined.filter(entity => entity.type === 'text').length,
    blockCount: (blocks || []).length,
    layerCount: new Set([...combined.map(entity => entity.layer), ...(blocks || []).map(block => block.layer)]).size,
    warningCount: warnings.length
  };
}

function objectLabel(object) {
  return object.articleNo || object.type || object.variant || object.id || object.refId || '';
}

function obstacleLabel(obstacle) {
  if (obstacle.type === 'pipe') return `Rohr ${Math.round(Number(obstacle.diameter) || 0)}`;
  if (obstacle.type === 'duct') return `Lueftung ${Math.round(Number(obstacle.width) || 0)}`;
  return obstacle.type || 'Hindernis';
}

function obstacleWidth(obstacle) {
  if (obstacle.type === 'pipe') return Math.max(1, Number(obstacle.diameter) || 1);
  return Math.max(1, Number(obstacle.width) || 1);
}

function defaultFilename(project = currentProject()) {
  const projectNo = cleanText(project?.meta?.projectNo || project?.projectNo || project?.number || '').replace(/\\s+/g, '_');
  const stamp = timestamp();
  return `${projectNo ? `${projectNo}_` : ''}KS-Studio_${stamp}.dxf`;
}

function timestamp() {
  const date = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`;
}

function currentProject() {
  return S.project || null;
}

function point2(x, y) {
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
  const px = Number(x);
  const py = Number(y);
  if (Math.abs(px) > MAX_DXF_COORDINATE_ABS || Math.abs(py) > MAX_DXF_COORDINATE_ABS) return null;
  return { x: px, y: py };
}

function pointFromPlan(point = {}) {
  return point2(point?.x, point?.y ?? point?.z);
}

function dxfPoint(point = {}) {
  const source = point2(point?.x, point?.y);
  return source ? { x: source.x, y: -source.y } : null;
}

function dxfRotation(rotation = 0) {
  const numeric = Number(rotation);
  if (!Number.isFinite(numeric)) return 0;
  const flipped = -numeric;
  return ((flipped % 360) + 360) % 360;
}

function metadataAnchor(entities = []) {
  const bounds = entityBounds(entities);
  if (!bounds) return { x: 0, y: 0 };
  return {
    x: bounds.maxX + 800,
    y: bounds.minY - 250
  };
}

function entityBounds(entities = []) {
  const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  let hasPoint = false;
  const includePoint = point => {
    if (!point || !isFiniteNumber(point.x) || !isFiniteNumber(point.y)) return;
    hasPoint = true;
    bounds.minX = Math.min(bounds.minX, Number(point.x));
    bounds.minY = Math.min(bounds.minY, Number(point.y));
    bounds.maxX = Math.max(bounds.maxX, Number(point.x));
    bounds.maxY = Math.max(bounds.maxY, Number(point.y));
  };
  const includeCircleBounds = entity => {
    const center = entity.center;
    const radius = Math.max(0, Number(entity.radius) || 0);
    includePoint({ x: Number(center?.x) - radius, y: Number(center?.y) - radius });
    includePoint({ x: Number(center?.x) + radius, y: Number(center?.y) + radius });
  };

  for (const entity of entities) {
    if (entity.type === 'line') {
      includePoint(entity.start);
      includePoint(entity.end);
    } else if (entity.type === 'polyline') {
      for (const point of entity.points || []) includePoint(point);
    } else if (entity.type === 'circle' || entity.type === 'arc') {
      includeCircleBounds(entity);
    } else if (entity.type === 'text' || entity.type === 'insert') {
      includePoint(entity.point);
    }
  }
  return hasPoint ? bounds : null;
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function centroid(points) {
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length
  };
}

function rotateAround(point, pivot, degrees) {
  const angle = (Number(degrees) || 0) * Math.PI / 180;
  if (!angle) return { ...point };
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos
  };
}

function layerName(value) {
  const cleaned = cleanText(value || DEFAULT_LAYER)
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '_')
    .slice(0, 31);
  return cleaned || DEFAULT_LAYER;
}

function authoritativeSystemOwner(object) {
  const owner = String(object?.owner || '').trim();
  const systemId = String(object?.systemId || '').trim();
  return owner && systemId && owner === systemId ? owner : '';
}

function systemLayerName(ownerId, localName = 'GEOMETRY') {
  const owner = layerName(ownerId || 'UNKNOWN');
  const local = layerName(localName || 'GEOMETRY');
  const prefix = `SYS_${owner.slice(0, 10)}_${stableNameHash(ownerId)}`;
  return `${prefix}_${local}`.slice(0, 31);
}

function systemTextStyleName(ownerId, localName = DEFAULT_TEXT_STYLE) {
  const local = textStyleName(localName || DEFAULT_TEXT_STYLE);
  return `S${stableNameHash(ownerId)}_${local}`.slice(0, 31);
}

function stableNameHash(value) {
  let hash = 2166136261;
  for (const char of String(value || '')) {
    hash ^= char.codePointAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase().padStart(6, '0').slice(-6);
}

function colorForLayer(layer) {
  if (CORE_COLOR_BY_LAYER[layer]) return CORE_COLOR_BY_LAYER[layer];
  const palette = [2, 3, 4, 5, 6, 8, 9];
  const hash = Number.parseInt(stableNameHash(layer), 36);
  return palette[hash % palette.length];
}

function shortWarningText(value) {
  return cleanText(value || 'unbekannt').slice(0, 120) || 'unbekannt';
}

function cleanText(value) {
  return String(value || '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^\x20-\x7e]/g, '')
    .trim();
}

function textStyleName(value) {
  return cleanText(value || DEFAULT_TEXT_STYLE)
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '_')
    .slice(0, 31) || DEFAULT_TEXT_STYLE;
}

function formatValue(value) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '0';
    return String(Math.round(value * 10000) / 10000);
  }
  return cleanText(value);
}

function isFiniteNumber(value) {
  return Number.isFinite(Number(value));
}

export {
  buildProjectDxf,
  downloadProject
};
